import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '@/i18n/useTranslation'
import { ROUTES, isRouteActive } from '@/lib/routes'
import { useUserStore } from '@/store/user-store'
import { useBalanceStore } from '@/store/balance-store'

export interface BottomNavItemData {
  href:  string
  label: string
}

export interface UseBottomNavReturn {
  navItems: BottomNavItemData[]
  isActive: (href: string) => boolean
  /** El balance activo tiene QR habilitado → el FAB central pasa a ser QR. */
  qrEnabled: boolean
  qrLabel:   string
  /** Activo durante 5 s tras el primer login — dispara el halo pulsante en el FAB. */
  showLoginGlow: boolean
  /** Solo true si estamos en un root de la navbar (no en rutas hijas). */
  isRootNav: boolean
}

/** Rutas exactas que muestran la navbar (roots del bottom nav). */
const NAV_ROOT_PATHS = new Set(
  ROUTES.filter(r => r.inBottomNav).map(r => r.path)
)

export function useBottomNav(): UseBottomNavReturn {
  const { pathname } = useLocation()
  const { t } = useTranslation('layout')

  const balances        = useUserStore(s => s.user.balances)
  const selectedBalance = useBalanceStore(s => s.selectedBalance)
  const activeBalance   = balances.find(b => b.id === selectedBalance) ?? balances[0]
  const qrEnabled       = !!activeBalance?.QrEnabled

  const navItems: BottomNavItemData[] = ROUTES
    .filter(r => r.inBottomNav)
    .map(r => ({ href: r.path, label: t(`layout.nav.${r.navKey}`) }))

  function isActive(href: string) {
    return isRouteActive(pathname, href)
  }

  const isRootNav = useMemo(() => NAV_ROOT_PATHS.has(pathname), [pathname])

  const [showLoginGlow, setShowLoginGlow] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem('fab-login-glow') !== '1') return
    sessionStorage.removeItem('fab-login-glow')
    setShowLoginGlow(true)
  }, [])

  return { navItems, isActive, qrEnabled, qrLabel: t('layout.nav.scanQr'), showLoginGlow, isRootNav }
}
